import { Component, Suspense } from 'react'
import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'

import { AlertTriangle, RefreshCw } from '@/components/icons'

class RouteErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false }

  static getDerivedStateFromError() {
    return { failed: true }
  }

  override render() {
    if (this.state.failed) {
      return (
        <div role="alert" className="bl-panel mx-auto mt-[8vh] max-w-md space-y-4 p-6 text-center text-[color:var(--bl-ink-2)] sm:p-8">
          <span aria-hidden="true" className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[rgb(var(--color-warning)/0.12)] text-[rgb(var(--color-text-warning))] ring-1 ring-[rgb(var(--color-warning)/0.3)]">
            <AlertTriangle size={20} />
          </span>
          <p>This page could not load. Reload to try again.</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 rounded-[var(--radius-md)] bg-[linear-gradient(180deg,rgb(var(--color-primary-hover)),rgb(var(--color-primary)))] px-4 py-2 text-sm font-semibold text-[rgb(var(--color-primary-contrast))] shadow-[0_8px_22px_-12px_var(--bl-glow)]"
          >
            <RefreshCw size={14} aria-hidden="true" />
            Reload page
          </button>
        </div>
      )
    }
    return this.props.children
  }
}

export function RoutePage({ children, resetOnNavigation = true }: {
  children: ReactNode
  resetOnNavigation?: boolean
}) {
  const { pathname } = useLocation()
  return (
    <RouteErrorBoundary key={resetOnNavigation ? pathname : 'layout'}>
      <Suspense
        fallback={
          <div className="flex min-h-[40vh] items-center justify-center">
            <p role="status" className="flex items-center gap-3 text-sm text-[color:var(--bl-ink-3)]">
              <span aria-hidden="true" className="bl-ember-spinner" />
              Loading page...
            </p>
          </div>
        }
      >
        {children}
      </Suspense>
    </RouteErrorBoundary>
  )
}
